/**
 * Hooks for the comments domain.
 */

import { useEffect, useRef } from "react";
import { useAsync, useMutation } from "./useAsync";
import * as api from "../lib/api";
import { supabase } from "../lib/supabase";

/** Fetch comments for a playlist or course and keep them fresh with Supabase Realtime. */
export function useComments(filter: { playlistId?: string; courseId?: string }) {
  const key = filter.playlistId ?? filter.courseId ?? null;
  const result = useAsync(
    () => api.getComments(filter),
    key ? [filter.playlistId ?? "", filter.courseId ?? ""] : null,
  );

  const revalidateRef = useRef(result.revalidate);
  revalidateRef.current = result.revalidate;
  const debounceRef = useRef<number | null>(null);
  const fallbackIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!key) return;

    const clearFallbackInterval = () => {
      if (fallbackIntervalRef.current !== null) {
        window.clearInterval(fallbackIntervalRef.current);
        fallbackIntervalRef.current = null;
      }
    };

    const ensureFallbackInterval = () => {
      if (fallbackIntervalRef.current !== null) return;
      fallbackIntervalRef.current = window.setInterval(() => {
        void revalidateRef.current();
      }, 30_000);
    };

    const scheduleRevalidate = () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        debounceRef.current = null;
        void revalidateRef.current();
      }, 150);
    };

    const channel = supabase.channel(
      `comments:${filter.playlistId ?? "none"}:${filter.courseId ?? "none"}:${Math.random().toString(36).slice(2)}`,
    );

    if (filter.playlistId) {
      channel.on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "playlist_comments",
          filter: `playlist_id=eq.${filter.playlistId}`,
        },
        scheduleRevalidate,
      );
    }

    if (filter.courseId) {
      channel.on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "playlist_comments",
          filter: `course_id=eq.${filter.courseId}`,
        },
        scheduleRevalidate,
      );
    }

    ensureFallbackInterval();
    channel.subscribe((status) => {
      if (status === "SUBSCRIBED") {
        clearFallbackInterval();
        return;
      }

      if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
        ensureFallbackInterval();
      }
    });

    return () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      clearFallbackInterval();
      void supabase.removeChannel(channel);
    };
  }, [filter.courseId, filter.playlistId, key]);

  return result;
}

/** Mutation: create an authenticated comment. */
export function useCreateComment() {
  return useMutation(
    (
      context: { playlistId?: string; courseId?: string },
      userId: string,
      displayName: string,
      body: string,
    ) => api.createComment(context, userId, displayName, body),
  );
}

/** Mutation: create a guest comment via edge function. */
export function useCreateGuestComment() {
  return useMutation(
    (
      token: string,
      tokenType: "playlist" | "course",
      displayName: string,
      body: string,
    ) => api.createGuestComment(token, tokenType, displayName, body),
  );
}

/** Mutation: update a comment body. */
export function useUpdateComment() {
  return useMutation((commentId: string, body: string) =>
    api.updateComment(commentId, body),
  );
}

/** Mutation: pin or unpin a comment. */
export function useTogglePinComment() {
  return useMutation((commentId: string, pinned: boolean) =>
    api.togglePinComment(commentId, pinned),
  );
}

/** Mutation: delete a comment. */
export function useDeleteComment() {
  return useMutation((commentId: string) => api.deleteComment(commentId));
}
